import { Button, useTranslation } from '@hbs-us/ui';
import { loadConnectAndInitialize, type StripeConnectInstance } from '@stripe/connect-js';
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js';
import { Check, X } from 'lucide-react';
import { type MouseEvent, useEffect, useId, useRef, useState } from 'react';

import { apiFetchJson } from '../../../lib/apiFetch.js';
import { readStoredLocale } from '../../../lib/locale.js';
import { ConfirmDialog } from '../../_money/ConfirmDialog.js';
import {
  useStripeConnectMutation,
  useStripeDisconnectMutation,
  useStripeReturnMutation,
} from '../useSettingsQuery.js';

/**
 * The Settings "Online Payments" card — the SPA face of the Stripe Connect flow
 * (21-12, reworked to embedded onboarding in 27.2-06).
 *
 * Four states, driven by the additive `stripeEnabled` + existing `stripeConnected` from
 * the coarse settings query:
 *   • !stripeEnabled → a short "not configured" note, no action.
 *   • enabled + !connected → a "Connect Stripe" button.
 *   • onboarding open → Stripe's own `ConnectAccountOnboarding` rendered INSIDE the app
 *     (C1) in a floating modal over the settings page, backed by a client_secret minted
 *     per session by POST /api/v1/settings/stripe/account-session. The modal is a native
 *     <dialog> opened with `showModal()` (the same primitive the shared Drawer uses), so
 *     the focus trap, Esc-to-close and inert background come from the platform; the
 *     backdrop click and the header close button are wired on top. Stripe still opens its
 *     OWN authentication window from inside the component for Express accounts — the
 *     modal only changes where the component renders.
 *   • enabled + connected → "ready to accept payments" + a destructive "Disconnect"
 *     behind ConfirmDialog.
 *
 * READINESS IS NEVER INFERRED FROM THE UI (T-27.2-06-01). `onExit` only means the user
 * left the flow; whether the account can actually charge is `charges_enabled`, which
 * lives at Stripe and reaches us through the payments authority. So both `onExit` and a
 * slow poll while the pane is open ask the server (POST /settings/stripe/return reads
 * `getConnectAccountStatus().chargesEnabled` and syncs the core mirror), and that is what
 * flips the card to ready on its own once Stripe finishes verification.
 *
 * If embedded onboarding cannot start — no publishable key on this build, or the Connect
 * bundle/secret fails — the card falls back to the original hosted redirect rather than
 * showing a dead pane. A failure that Stripe reports through `onLoadError` BEFORE
 * `onLoaderStart` gets the same treatment, because at that point the pane has rendered
 * nothing and Stripe shows no message of its own; after the pane has painted, Stripe's
 * own in-pane error is better than yanking the modal away.
 *
 * Return-leg detection: on mount this reads the `?stripe` query param (the settings route
 * declares no search schema, so we parse window.location directly). `return` → finalize
 * once then strip the param so a reload does not re-run it; `refresh` (expired link) →
 * surface a resume affordance.
 *
 * Disconnect clears the LOCAL Connect fields only — it never revokes at Stripe (C5), so
 * reconnecting stays one click away. Every color is a locked @hbs-us/ui token (BRAND-06);
 * the card never sends a tenant id (SEC-11 — the server resolves it).
 */

const SECTION = 'rounded-md border border-border bg-card p-6';
const SECTION_HEADING = 'mb-1 font-display font-semibold text-foreground text-lg';
const SECTION_DESC = 'mb-4 text-muted-foreground text-sm';

/** How often we re-ask the server for charges_enabled while the modal is open. */
const READINESS_POLL_MS = 8000;

/**
 * Ceiling for the onboarding modal. Applied to the dialog AND to the flex column inside
 * it — the column needs the same bound for its body to become the scroller instead of
 * pushing the dialog past the viewport. Generous, because Stripe's steps are tall.
 */
const DIALOG_MAX_H = 'max-h-[85vh]';

/**
 * `POST /api/v1/settings/stripe/account-session` — Admin-only; mints a single-use Account
 * Session client_secret scoped to the account_onboarding component.
 *
 * TODO: swap to @hbs-us/contracts after next publish.
 */
interface ConnectAccountSessionResponse {
  ok: true;
  clientSecret: string;
  accountId: string;
}

function readStripeParam(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('stripe');
}

function stripStripeParam(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('stripe');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

/** Read at call time (not module load) so a build without the key still renders. */
function publishableKey(): string {
  return String(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '').trim();
}

export function StripeConnectCard({
  stripeEnabled,
  stripeConnected,
}: {
  stripeEnabled: boolean;
  stripeConnected: boolean;
}) {
  const { t } = useTranslation();
  const connect = useStripeConnectMutation();
  const disconnect = useStripeDisconnectMutation();
  const finalize = useStripeReturnMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [needsResume, setNeedsResume] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [embeddedFailed, setEmbeddedFailed] = useState(false);
  /**
   * ONE Connect instance for the life of this card, reused every time the modal
   * reopens. Stripe requires a single instance per session — each
   * `loadConnectAndInitialize` mints another account session, so building one per
   * click both wastes sessions and is the mistake the docs call out by name.
   *
   * It lives in a ref rather than a module singleton on purpose: signing out
   * unmounts this tree, so the instance is collected with it. That is also why
   * nothing here calls `logout()` — Stripe explicitly warns against calling it on
   * unmount, since it invalidates the whole account session.
   */
  const connectInstanceRef = useRef<StripeConnectInstance | null>(null);
  /** Whether Stripe's pane has painted anything yet — decides who renders a load error. */
  const loaderStartedRef = useRef(false);
  const finalizeRan = useRef(false);
  const syncReadinessRef = useRef(finalize.mutate);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dialogTitleId = useId();

  useEffect(() => {
    syncReadinessRef.current = finalize.mutate;
  });

  // Run the return/refresh detection exactly once on mount. The guard + param strip
  // together stop a reload from re-running the finalize.
  useEffect(() => {
    if (finalizeRan.current) return;
    const param = readStripeParam();
    if (param !== 'return' && param !== 'refresh') return;
    finalizeRan.current = true;

    if (param === 'return') {
      if (stripeEnabled) finalize.mutate();
    } else {
      setNeedsResume(true);
    }
    stripStripeParam();
  }, [stripeEnabled, finalize]);

  // While the onboarding modal is open, keep asking the server whether Stripe has verified
  // the account. This is what flips the card to ready with no second click from the user,
  // and it stops the moment the answer is yes (or the modal closes).
  useEffect(() => {
    if (!onboardingOpen || stripeConnected) return;
    const timer = window.setInterval(() => syncReadinessRef.current(), READINESS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [onboardingOpen, stripeConnected]);

  // Once the account really is ready, the modal has nothing left to collect — it closes
  // itself, which is what makes the poll worth running.
  useEffect(() => {
    if (stripeConnected) setOnboardingOpen(false);
  }, [stripeConnected]);

  // Open the onboarding modal for real (top layer + focus trap + inert background) and
  // hold the page still underneath it for as long as it is mounted.
  useEffect(() => {
    if (!onboardingOpen) return;
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [onboardingOpen]);

  /** Every close path is the same: hide the modal, then ask the server if it is ready. */
  function closeOnboarding() {
    setOnboardingOpen(false);
    syncReadinessRef.current();
  }

  /**
   * Stripe renders its own message for anything that fails AFTER the pane has painted,
   * so the only case we have to cover is a failure before `onLoaderStart` — there the
   * component has drawn nothing and the user would be left staring at an empty modal.
   * Then we close the modal and hand the user back to the card's existing error line and
   * hosted-redirect route. Idempotent, because Stripe may call this more than once.
   */
  function onOnboardingLoadError() {
    if (loaderStartedRef.current) return;
    // The instance never became usable, so drop it and let a retry build a fresh one.
    connectInstanceRef.current = null;
    setOnboardingOpen(false);
    setEmbeddedFailed(true);
  }

  function onDialogClick(event: MouseEvent<HTMLDialogElement>) {
    // The panel fills the dialog box, so the dialog itself is only ever the backdrop.
    if (event.target === dialogRef.current) closeOnboarding();
  }

  function startHostedConnect() {
    setNeedsResume(false);
    connect.mutate(undefined, {
      onSuccess: (result) => {
        if (result.onboardingUrl) window.location.assign(result.onboardingUrl);
      },
    });
  }

  function startConnect() {
    setNeedsResume(false);
    setEmbeddedFailed(false);
    const key = publishableKey();
    if (!key) {
      startHostedConnect();
      return;
    }
    try {
      // Built once and reused — see connectInstanceRef. Stripe re-calls
      // fetchClientSecret itself whenever the session expires, so this MUST mint a
      // brand-new account session every call rather than hand back a cached secret.
      connectInstanceRef.current ??= loadConnectAndInitialize({
        publishableKey: key,
        locale: readStoredLocale(),
        fetchClientSecret: async () => {
          const session = await apiFetchJson<ConnectAccountSessionResponse>(
            '/api/v1/settings/stripe/account-session',
            { method: 'POST' },
          );
          return session.clientSecret;
        },
      });
      loaderStartedRef.current = false;
      setOnboardingOpen(true);
    } catch {
      connectInstanceRef.current = null;
      setOnboardingOpen(false);
      setEmbeddedFailed(true);
    }
  }

  const connectInstance = connectInstanceRef.current;

  return (
    <section className={SECTION}>
      <h2 className={SECTION_HEADING}>{t('settings:stripe.heading')}</h2>
      <p className={SECTION_DESC}>{t('settings:stripe.description')}</p>

      {!stripeEnabled ? (
        <p className="text-muted-foreground text-sm">{t('settings:stripe.notConfigured')}</p>
      ) : stripeConnected ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 font-medium text-foreground text-sm">
            <Check className="size-4 text-muted-foreground" aria-hidden="true" />
            {t('settings:stripe.ready')}
          </span>
          <Button
            type="button"
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            disabled={disconnect.isPending}
          >
            {t('settings:stripe.disconnect')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" onClick={startConnect} disabled={connect.isPending}>
            {needsResume ? t('settings:stripe.resume') : t('settings:stripe.connect')}
          </Button>
          {embeddedFailed ? (
            <Button type="button" variant="secondary" onClick={startHostedConnect}>
              {t('settings:stripe.continueOnStripe')}
            </Button>
          ) : null}
          {needsResume ? (
            <p className="text-muted-foreground text-sm">{t('settings:stripe.resumeNote')}</p>
          ) : null}
        </div>
      )}

      {embeddedFailed ? (
        <p role="alert" className="mt-3 text-destructive text-sm">
          {t('settings:stripe.onboardingError')}
        </p>
      ) : null}
      {finalize.isError ? (
        <p role="alert" className="mt-3 text-destructive text-sm">
          {t('settings:stripe.returnError')}
        </p>
      ) : null}
      {connect.isError ? (
        <p role="alert" className="mt-3 text-destructive text-sm">
          {t('settings:stripe.connectError')}
        </p>
      ) : null}
      {disconnect.isError ? (
        <p role="alert" className="mt-3 text-destructive text-sm">
          {t('settings:stripe.disconnectError')}
        </p>
      ) : null}

      {onboardingOpen && connectInstance ? (
        // biome-ignore lint/a11y/useKeyWithClickEvents: this handler only serves backdrop clicks; the keyboard routes out are Esc (native) and the close button.
        <dialog
          ref={dialogRef}
          onClose={closeOnboarding}
          onClick={onDialogClick}
          aria-labelledby={dialogTitleId}
          className={`fixed inset-0 m-auto ${DIALOG_MAX_H} w-[min(44rem,calc(100vw-2rem))] overflow-hidden rounded-md border border-border bg-card text-card-foreground shadow-lg backdrop:bg-[var(--overlay)]`}
        >
          <div className={`flex ${DIALOG_MAX_H} flex-col`}>
            <div className="flex items-start justify-between gap-4 border-border border-b px-6 py-4">
              <div className="min-w-0">
                <h2
                  id={dialogTitleId}
                  className="font-display font-semibold text-foreground text-lg"
                >
                  {t('settings:stripe.onboardingHeading')}
                </h2>
                <p className="mt-1 text-muted-foreground text-sm">
                  {t('settings:stripe.onboardingNote')}
                </p>
              </div>
              <button
                type="button"
                onClick={closeOnboarding}
                aria-label={t('settings:stripe.onboardingClose')}
                className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <ConnectComponentsProvider connectInstance={connectInstance}>
                {/*
                  onExit means "the user left the flow", nothing more. The readiness answer
                  comes from the server, so all this does is ask early instead of waiting
                  for the next poll.
                */}
                <ConnectAccountOnboarding
                  onExit={() => syncReadinessRef.current()}
                  onLoaderStart={() => {
                    loaderStartedRef.current = true;
                  }}
                  onLoadError={onOnboardingLoadError}
                />
              </ConnectComponentsProvider>
            </div>
          </div>
        </dialog>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        heading={t('settings:stripe.disconnectConfirmHeading')}
        body={t('settings:stripe.disconnectConfirmBody')}
        confirmLabel={t('settings:stripe.disconnectConfirmCta')}
        cancelLabel={t('settings:stripe.disconnectConfirmCancel')}
        confirmPending={disconnect.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => disconnect.mutate(undefined, { onSuccess: () => setConfirmOpen(false) })}
      />
    </section>
  );
}
